(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-ugc-carousel';
  var TRACK_CLASS = 'oddit-section-ugc-carousel__track';
  var DOT_CLASS = 'oddit-section-ugc-carousel__dot';
  var DOT_ACTIVE_CLASS = 'oddit-section-ugc-carousel__dot--active';
  var GRABBING_CLASS = 'oddit-section-ugc-carousel__track--grabbing';
  var CLONE_CLASS = 'oddit-section-ugc-carousel__card--clone';
  var sectionState = new Map();

  function initSection(section) {
    if (!section || section.dataset.odditInitialized === 'true') return;
    section.dataset.odditInitialized = 'true';

    var track = section.querySelector('.' + TRACK_CLASS);
    var prevBtn = section.querySelector('[data-ugc-prev]');
    var nextBtn = section.querySelector('[data-ugc-next]');
    var indicators = section.querySelector('.oddit-section-ugc-carousel__dots');
    var cards = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-ugc-carousel__card')
    );

    if (!track || cards.length === 0) return;

    var dots = [];
    var originalCount = cards.length;
    var loopEnabled = false;
    var loopWidth = 0;

    var state = {
      userTookControl: false,
      autoplay: null,
      // True while a card video is in full (clicked) playback — pauses autoplay
      // so the carousel doesn't advance out from under the shopper.
      videoPlaying: false,
      // Treat any scroll happening before this timestamp as programmatic
      // (autoplay / arrow / dot / wrap driven) rather than a manual scroll.
      programmaticUntil: 0
    };

    /* ---- Geometry helpers ---- */

    // Distance between the start of one card and the next (card width + gap).
    function getStep() {
      if (cards.length < 2) {
        return cards[0] ? cards[0].getBoundingClientRect().width : 0;
      }
      var a = cards[0].getBoundingClientRect();
      var b = cards[1].getBoundingClientRect();
      return b.left - a.left;
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function isScrollable() {
      return maxScroll() > 2;
    }

    function atStart() {
      return track.scrollLeft <= 2;
    }

    function atEnd() {
      return track.scrollLeft >= maxScroll() - 2;
    }

    function scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    function markProgrammatic() {
      // Cover the duration of a smooth scroll so the scroll handler doesn't
      // mistake it (or an invisible wrap) for the user taking control.
      state.programmaticUntil = performance.now() + 700;
    }

    /* ---- Infinite loop: clone a full card set on each side ---- */

    function stripReveal(el) {
      el.classList.remove('oddit-reveal');
      el.style.opacity = '';
      el.style.transform = '';
    }

    function prepClone(node) {
      node.classList.add(CLONE_CLASS);
      node.setAttribute('aria-hidden', 'true');
      node.removeAttribute('data-ugc-card');
      // Late-added clones are never seen by the reveal observer, so drop the
      // reveal classes or they'd stay stuck in their hidden pre-reveal state.
      stripReveal(node);
      node.querySelectorAll('.oddit-reveal').forEach(stripReveal);
      // Keep clones out of the tab order — they're duplicate content.
      node.querySelectorAll('a, button').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
    }

    function setupLoop() {
      var step = getStep();
      if (step <= 0) {
        loopEnabled = false;
        return;
      }
      // Only loop when the real cards actually overflow the viewport.
      if (step * originalCount - track.clientWidth <= 2) {
        loopEnabled = false;
        return;
      }

      loopEnabled = true;
      loopWidth = step * originalCount;

      var leading = document.createDocumentFragment();
      var trailing = document.createDocumentFragment();
      cards.forEach(function (card) {
        var before = card.cloneNode(true);
        prepClone(before);
        leading.appendChild(before);

        var after = card.cloneNode(true);
        prepClone(after);
        trailing.appendChild(after);
      });
      track.insertBefore(leading, cards[0]);
      track.appendChild(trailing);

      // Park on the real set (originals sit between the two clone sets).
      markProgrammatic();
      track.scrollLeft = loopWidth;
    }

    // Keep scrollLeft within the middle (real) band so the clone sets on
    // either side make scrolling appear endless. The jump is invisible because
    // the clone at the boundary is identical to the original it lands on.
    function normalizeScroll() {
      if (!loopEnabled || loopWidth <= 0) return;
      var x = track.scrollLeft;
      if (x >= 2 * loopWidth) {
        track.scrollLeft = x - loopWidth;
      } else if (x < loopWidth) {
        track.scrollLeft = x + loopWidth;
      }
    }

    // Number of dots: one per real card when looping (so they cycle back to
    // the start), otherwise one per scroll stop.
    function getPageCount() {
      if (loopEnabled) return originalCount;
      var step = getStep();
      if (step <= 0) return 1;
      var max = maxScroll();
      if (max <= 2) return 1;
      return Math.round(max / step) + 1;
    }

    function getActiveIndex() {
      var step = getStep();
      if (step <= 0) return 0;
      if (loopEnabled) {
        var rel = Math.round((track.scrollLeft - loopWidth) / step);
        return ((rel % originalCount) + originalCount) % originalCount;
      }
      var idx = Math.round(track.scrollLeft / step);
      return Math.max(0, Math.min(idx, dots.length - 1));
    }

    function scrollToIndex(index) {
      var step = getStep();
      if (step <= 0) return;
      normalizeScroll();
      var base = loopEnabled ? loopWidth : 0;
      var target = base + step * index;
      if (!loopEnabled) {
        var max = maxScroll();
        if (target > max) target = max;
      }
      if (target < 0) target = 0;
      markProgrammatic();
      track.scrollTo({ left: target, behavior: scrollBehavior() });
    }

    function step(direction) {
      var amount = getStep() * direction;
      normalizeScroll();
      markProgrammatic();
      track.scrollBy({ left: amount, behavior: scrollBehavior() });
    }

    /* ---- Dots: rebuilt so their count matches the page count ---- */
    function buildDots() {
      if (!indicators) return;
      var count = getPageCount();
      if (dots.length === count) return;

      indicators.innerHTML = '';
      dots = [];

      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = DOT_CLASS;
        dot.setAttribute('aria-label', 'Go to slide group ' + (i + 1));
        dot.setAttribute('data-ugc-dot', String(i));

        var inner = document.createElement('span');
        inner.setAttribute('aria-hidden', 'true');
        inner.innerHTML = '&zwnj;';
        dot.appendChild(inner);

        (function (index) {
          dot.addEventListener('click', function () {
            scrollToIndex(index);
            disableAutoplayPermanently();
          });
          dot.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              var n = (index + 1) % dots.length;
              dots[n].focus();
              scrollToIndex(n);
              disableAutoplayPermanently();
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              var p = (index - 1 + dots.length) % dots.length;
              dots[p].focus();
              scrollToIndex(p);
              disableAutoplayPermanently();
            }
          });
        })(i);

        indicators.appendChild(dot);
        dots.push(dot);
      }
    }

    function updateControls() {
      if (loopEnabled) {
        // Looping never reaches an end — both arrows and the dots always show.
        if (prevBtn) prevBtn.hidden = false;
        if (nextBtn) nextBtn.hidden = false;
        if (indicators) indicators.hidden = false;
      } else {
        var scrollable = isScrollable();
        if (prevBtn) prevBtn.hidden = !scrollable || atStart();
        if (nextBtn) nextBtn.hidden = !scrollable || atEnd();
        if (indicators) indicators.hidden = !scrollable;
      }

      var active = getActiveIndex();
      dots.forEach(function (dot, i) {
        if (i === active) {
          dot.classList.add(DOT_ACTIVE_CLASS);
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove(DOT_ACTIVE_CLASS);
          dot.removeAttribute('aria-current');
        }
      });
    }

    function recalc() {
      if (loopEnabled) {
        // Card width (and therefore the loop width) changes across breakpoints.
        var active = getActiveIndex();
        loopWidth = getStep() * originalCount;
        markProgrammatic();
        track.scrollLeft = loopWidth + active * getStep();
      }
      buildDots();
      updateControls();
    }

    function disableAutoplayPermanently() {
      if (state.userTookControl) return;
      state.userTookControl = true;
      if (state.autoplay) state.autoplay.stop();
    }

    /* ---- Autoplay (RAF-based) ---- */
    function initAutoplay() {
      var autoplayEnabled = section.dataset.autoplay === 'true';
      if (!autoplayEnabled) return null;

      var speedSeconds = parseInt(section.dataset.autoplaySpeed, 10) || 5;
      var intervalMs = speedSeconds * 1000;
      var rafId = null;
      var lastAdvance = performance.now();

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reducedMotion.matches) {
        return { start: function () {}, stop: function () {}, destroy: function () {} };
      }

      function tick(now) {
        if (state.userTookControl) return;
        if (now - lastAdvance >= intervalMs) {
          if (loopEnabled) {
            // Endless: just keep advancing one card; wrapping is invisible.
            step(1);
          } else {
            var count = getPageCount();
            var current = getActiveIndex();
            var next = current >= count - 1 ? 0 : current + 1;
            scrollToIndex(next);
          }
          lastAdvance = now;
        }
        rafId = requestAnimationFrame(tick);
      }

      function start() {
        if (state.userTookControl) return;
        if (state.videoPlaying) return;
        if (rafId) return;
        lastAdvance = performance.now();
        rafId = requestAnimationFrame(tick);
      }

      function stop() {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      }

      track.addEventListener('mouseenter', function () {
        if (!state.userTookControl) stop();
      });
      track.addEventListener('mouseleave', function () {
        if (!state.userTookControl) start();
      });

      track.addEventListener('focusin', disableAutoplayPermanently);
      track.addEventListener('touchstart', disableAutoplayPermanently, { passive: true });

      document.addEventListener('visibilitychange', function () {
        if (state.userTookControl) return;
        if (document.hidden) stop();
        else start();
      });

      reducedMotion.addEventListener('change', function (e) {
        if (state.userTookControl) return;
        if (e.matches) stop();
        else start();
      });

      start();

      return { start: start, stop: stop, destroy: stop };
    }

    /* ---- Desktop click-and-drag (mouse only; touch scrolls natively) ---- */
    function initDrag() {
      var DRAG_THRESHOLD = 6;
      var isDown = false;
      var dragging = false;
      var suppressClick = false;
      var startX = 0;
      var startScroll = 0;
      var pointerId = null;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        isDown = true;
        dragging = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        pointerId = e.pointerId;
      });

      track.addEventListener('pointermove', function (e) {
        if (!isDown || e.pointerId !== pointerId) return;
        var dx = e.clientX - startX;
        if (!dragging) {
          // Stay a "click" until the pointer clearly moves — keeps cards clickable.
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          // Nothing to scroll — leave clicks alone.
          if (track.scrollWidth - track.clientWidth <= 2) return;
          dragging = true;
          disableAutoplayPermanently();
          track.classList.add(GRABBING_CLASS);
          try { track.setPointerCapture(pointerId); } catch (err) {}
        }
        e.preventDefault();
        track.scrollLeft = startScroll - dx;
        // Wrap mid-drag so a long drag keeps looping; shift the anchor too so
        // continued movement stays consistent.
        if (loopEnabled) {
          var x = track.scrollLeft;
          if (x >= 2 * loopWidth) {
            track.scrollLeft = x - loopWidth;
            startScroll -= loopWidth;
          } else if (x < loopWidth) {
            track.scrollLeft = x + loopWidth;
            startScroll += loopWidth;
          }
        }
      });

      function endDrag(e) {
        if (!isDown || (pointerId !== null && e.pointerId !== pointerId)) return;
        isDown = false;
        if (dragging) {
          track.classList.remove(GRABBING_CLASS);
          try { track.releasePointerCapture(pointerId); } catch (err) {}
          // Swallow the click that the browser fires after a drag so cards
          // don't navigate when the user only meant to scroll.
          suppressClick = true;
        }
        dragging = false;
        pointerId = null;
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      // Capture phase so we beat the link's own click/navigation.
      track.addEventListener('click', function (e) {
        if (suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          suppressClick = false;
        }
      }, true);

      track.addEventListener('dragstart', function (e) {
        if (dragging) e.preventDefault();
      });
    }

    /* ---- Video cards: hover preview + click-to-play ---- */
    function initVideos() {
      var PREVIEW_SECONDS = 3;
      var wraps = Array.prototype.slice.call(
        section.querySelectorAll('[data-ugc-video-wrap]')
      );
      if (!wraps.length) return;

      var controllers = [];

      function resetOthers(current) {
        controllers.forEach(function (c) {
          if (c !== current) c.reset();
        });
      }

      wraps.forEach(function (wrap) {
        var video = wrap.querySelector('[data-ugc-video]');
        var toggle = wrap.querySelector('[data-ugc-video-toggle]');
        if (!video) return;

        var srcLoaded = false;
        var fullPlayback = false;
        var previewing = false;
        var controller = { reset: reset };
        controllers.push(controller);

        // Source is set on first use so videos (and their clones) don't
        // download until the shopper actually hovers or plays them.
        function ensureSrc() {
          if (srcLoaded) return;
          var source = video.querySelector('source[data-src]');
          if (source) source.src = source.getAttribute('data-src');
          video.load();
          srcLoaded = true;
        }

        function safePlay() {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }

        function seekStart() {
          try { video.currentTime = 0; } catch (e) {}
        }

        function reset() {
          fullPlayback = false;
          previewing = false;
          video.controls = false;
          video.muted = true;
          video.pause();
          seekStart();
          wrap.classList.remove('is-previewing', 'is-playing');
        }

        function startPreview() {
          if (fullPlayback) return;
          ensureSrc();
          previewing = true;
          video.muted = true;
          video.controls = false;
          seekStart();
          wrap.classList.add('is-previewing');
          safePlay();
        }

        function stopPreview() {
          if (fullPlayback) return;
          previewing = false;
          video.pause();
          seekStart();
          wrap.classList.remove('is-previewing');
        }

        function startFull() {
          ensureSrc();
          resetOthers(controller);
          previewing = false;
          fullPlayback = true;
          wrap.classList.remove('is-previewing');
          wrap.classList.add('is-playing');
          video.muted = false;
          video.controls = true;
          seekStart();
          state.videoPlaying = true;
          if (state.autoplay) state.autoplay.stop();
          safePlay();
        }

        function endFull() {
          reset();
          state.videoPlaying = false;
          if (state.autoplay && !state.userTookControl) state.autoplay.start();
        }

        // Loop the preview over the first few seconds (and loop short clips).
        video.addEventListener('timeupdate', function () {
          if (previewing && !fullPlayback && video.currentTime >= PREVIEW_SECONDS) {
            seekStart();
          }
        });

        video.addEventListener('ended', function () {
          if (fullPlayback) {
            endFull();
          } else if (previewing) {
            seekStart();
            safePlay();
          }
        });

        // Hover = muted preview (mouse only; touch users tap to play).
        wrap.addEventListener('mouseenter', startPreview);
        wrap.addEventListener('mouseleave', stopPreview);

        function activate(e) {
          if (fullPlayback) return;
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          startFull();
        }
        if (toggle) toggle.addEventListener('click', activate);
      });
    }

    /* ---- Wire up arrow controls ---- */
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        step(1);
        disableAutoplayPermanently();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        step(-1);
        disableAutoplayPermanently();
      });
    }

    /* ---- Keep UI in sync with scrolling / swiping ---- */
    var scrollRaf = null;
    track.addEventListener(
      'scroll',
      function () {
        var manual = performance.now() >= state.programmaticUntil;
        if (manual) {
          if (loopEnabled) normalizeScroll();
          if (!state.userTookControl) disableAutoplayPermanently();
        }
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(updateControls);
      },
      { passive: true }
    );

    /* ---- Recalc on resize ---- */
    var resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(recalc);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', recalc);
    }

    /* ---- Init ---- */
    setupLoop();
    recalc();
    initDrag();
    state.autoplay = initAutoplay();
    initVideos();

    sectionState.set(section, {
      autoplay: state.autoplay,
      resizeObserver: resizeObserver
    });
  }

  function destroySection(section) {
    var s = sectionState.get(section);
    if (!s) return;
    if (s.autoplay) s.autoplay.destroy();
    if (s.resizeObserver) s.resizeObserver.disconnect();
    sectionState.delete(section);
    section.dataset.odditInitialized = '';
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector
      ? e.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector
      ? e.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) destroySection(section);
  });
})();
